"use client";

import { cn } from "@/lib/utils";
import { IBlocks, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import { SHADOW_OPTIONS } from "./utils/block-style-classes";
interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockFourthWallProducts({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    let storeButtonStyle = {
        ...style,
        background: style.background,
        color: style.color,
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [data, setData] = React.useState<any>({
        uniqueItems: [],
    });
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const ids = blockUpdate.options?.items
                    ?.map((item: any) => item.id)
                    .join(",");
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_API_SERVER}/fourthwall/${blockUpdate.url.replaceAll("https://", "")}/unique-items?items=${ids}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                if (!data.error) {
                    setData(data);
                } else {
                    setData({
                        uniqueItems: [],
                    });
                }
            } catch (error) {
                console.error(error);
                setData({
                    uniqueItems: [],
                });
            }
        };
        fetchData();
    }, [account, blockUpdate.options?.itemsm, blockUpdate.url]);
    const items = data?.uniqueItems || [];
    const visibleItems = blockUpdate.options?.items
        ?.map((item: any) => {
            const matchingItem = items.find((i: any) => i.id === item.id);
            if (matchingItem) {
                // Return the matching item with its original 'sort' value
                return {
                    ...matchingItem,
                    sort: item.sort, // Ensure the 'sort' value remains intact
                };
            }
            return null; // In case no matching item is found
        })
        .filter(Boolean); // Filter out any null values in case of no matches
    const getGridCols = (itemCount: number) => {
        switch (itemCount) {
            case 1:
                return "grid-cols-3 [&>*:first-child]:col-span-3";
            case 2:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:last-child]:col-span-1";
            case 3:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*]:col-span-1 [&>*:last-child]:col-span-full";
            case 4:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:last-child]:col-span-2";
            case 5:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-1 [&>*:last-child]:col-span-1";
            case 6:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:last-child]:col-span-1";
            case 7:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2";
            case 8:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 9:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 10:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 ";
            default:
                return "grid-cols-3 [&>*:nth-child(3n+1)]:col-span-1 [&>*:nth-child(3n+2)]:col-span-1 [&>*:nth-child(3n)]:col-span-1";
        }
    };
    const productBackground = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_background
        : theme.profile.colors.primary;
    const productText = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_text
        : theme.profile.colors.primary_text;
    if (blockUpdate.options?.custom_theme) {
        storeButtonStyle.background =
            blockUpdate.options?.store_button_background;
        storeButtonStyle.color = blockUpdate.options?.store_button_text;
    }
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Fourthwall Products"}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div
                key={block.id}
                className={cn(
                    `relative flex w-full flex-row flex-wrap gap-2 rounded-user p-2`,
                    "block-" + block.id
                )}
                id="FourthwallProducts"
            >
                <div
                    className={cn(
                        "grid w-full grid-cols-1 gap-1",
                        getGridCols(visibleItems.length)
                    )}
                >
                    {visibleItems.map((item: any) => (
                        <a
                            href={`${item.link}?utm_source=doras-profile&utm_medium=profile-click`}
                            target="_blank"
                            // rel=""
                            key={item.id}
                            className={cn(
                                "relative flex scale-95 flex-col items-center gap-0 rounded-user transition-transform duration-300 hover:scale-100"
                            )}
                        >
                            <img
                                src={item.image}
                                alt={item.title}
                                className={cn(
                                    "h-64 w-full overflow-hidden rounded-user object-cover"
                                )}
                            />
                            <div
                                className={cn(
                                    "absolute inset-x-0 bottom-0 flex w-full flex-col items-start gap-2 rounded-user px-1",
                                    "rounded-t-none"
                                )}
                                style={{
                                    background: productBackground,
                                    color: productText,
                                }}
                            >
                                <h3 className="w-full truncate text-ellipsis text-lg font-semibold">
                                    {item.title}
                                </h3>
                                <p
                                    className="text-sm"
                                    style={{
                                        color: productText,
                                    }}
                                >
                                    {item.price}
                                </p>
                            </div>
                        </a>
                    ))}
                </div>
                <a className={`w-full`} href={blockUpdate.url} target="_blank">
                    <button
                        id="store-button"
                        className={cn(
                            "rounded-user bg-user-primary-color text-user-primary-text-color",
                            SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                            "w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                            blockUpdate.name.replaceAll(" ", "-")
                        )}
                        style={storeButtonStyle}
                    >
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            See More
                        </span>
                    </button>
                </a>
            </div>
        </AdminPopover>
    );
}
